import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import MUNICIPIOS_MX from '../../data/mexicoMunicipios';

const font = "'DM Sans', sans-serif";

const selectStyle = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  color: '#fff',
  fontSize: 14,
  fontFamily: font,
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const inputStyle = {
  ...selectStyle,
  cursor: 'text',
  appearance: 'auto',
  WebkitAppearance: 'auto',
};

const labelStyle = {
  fontSize: 12,
  color: 'rgba(255,255,255,0.55)',
  margin: '0 0 6px',
  fontFamily: font,
  fontWeight: 500,
};

/**
 * LocationCascade — Selector cascada adaptativo de ubicación.
 * País → Estado → Municipio/Ciudad (con autocomplete local para México)
 */
export default function LocationCascade({ value = {}, onChange, isMobile = false }) {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [labels, setLabels] = useState({ region: 'Estado', city: 'Municipio / Ciudad' });

  // Autocomplete state for municipio
  const [muniQuery, setMuniQuery] = useState('');
  const [muniResults, setMuniResults] = useState([]);
  const [showMuniDropdown, setShowMuniDropdown] = useState(false);
  const [selectedMuni, setSelectedMuni] = useState(value.cityName || '');
  const muniRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (muniRef.current && !muniRef.current.contains(e.target)) {
        setShowMuniDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cargar países al montar
  useEffect(() => {
    supabase
      .from('countries')
      .select('id, code, name, region_label, city_label')
      .order('name')
      .then(({ data }) => {
        if (data) {
          const mx = data.filter(c => c.code === 'MX');
          const rest = data.filter(c => c.code !== 'MX');
          setCountries([...mx, ...rest]);
        }
      });
  }, []);

  // Cargar regiones cuando cambia país
  useEffect(() => {
    if (!value.countryId) {
      setRegions([]);
      return;
    }
    const country = countries.find(c => c.id === value.countryId);
    if (country) {
      setLabels({
        region: country.region_label || 'Estado',
        city: country.code === 'MX' ? 'Municipio / Ciudad' : (country.city_label || 'Ciudad'),
      });
    }
    supabase
      .from('regions')
      .select('id, name, code')
      .eq('country_id', value.countryId)
      .order('name')
      .then(({ data }) => {
        if (data) setRegions(data);
      });
    setSelectedMuni('');
    setMuniQuery('');
  }, [value.countryId, countries]);

  // Reset municipio when region changes
  useEffect(() => {
    setSelectedMuni('');
    setMuniQuery('');
  }, [value.regionId]);

  // Check if current country is Mexico
  const isMexico = countries.find(c => c.id === value.countryId)?.code === 'MX';

  // Get local municipios for selected region
  const localMunicipios = (isMexico && value.regionId) ? (MUNICIPIOS_MX[value.regionId] || []) : [];

  // Filter municipios based on query
  const filterMunis = (query) => {
    if (!query || query.length < 1) return localMunicipios.slice(0, 15);
    const q = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return localMunicipios
      .filter(m => m.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(q))
      .slice(0, 10);
  };

  const handleCountry = (e) => {
    const countryId = e.target.value ? Number(e.target.value) : null;
    onChange({ countryId, regionId: null, cityId: null, cityName: null });
  };

  const handleRegion = (e) => {
    const regionId = e.target.value ? Number(e.target.value) : null;
    onChange({ ...value, regionId, cityId: null, cityName: null });
  };

  const handleMuniInput = (e) => {
    const q = e.target.value;
    setMuniQuery(q);
    setMuniResults(filterMunis(q));
    setShowMuniDropdown(true);
    setSelectedMuni('');
    onChange({ ...value, cityId: null, cityName: null });
  };

  const handleMuniSelect = (muni) => {
    setSelectedMuni(muni);
    setMuniQuery(muni);
    setShowMuniDropdown(false);
    onChange({ ...value, cityName: muni });
  };

  const handleMuniFocus = () => {
    setMuniResults(filterMunis(muniQuery));
    setShowMuniDropdown(true);
  };

  const gap = isMobile ? 12 : 14;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {/* País */}
      <div>
        <p style={labelStyle}>País</p>
        <select value={value.countryId || ''} onChange={handleCountry} style={selectStyle}>
          <option value="">Selecciona país</option>
          {countries.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Estado */}
      {value.countryId && (
        <div>
          <p style={labelStyle}>{labels.region}</p>
          <select value={value.regionId || ''} onChange={handleRegion} style={selectStyle}>
            <option value="">Selecciona {labels.region.toLowerCase()}</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Municipio / Ciudad — autocomplete para México */}
      {value.regionId && isMexico && localMunicipios.length > 0 && (
        <div ref={muniRef} style={{ position: 'relative' }}>
          <p style={labelStyle}>{labels.city}</p>
          <input
            type="text"
            value={muniQuery}
            onChange={handleMuniInput}
            onFocus={handleMuniFocus}
            placeholder="Escribe para buscar..."
            style={{
              ...inputStyle,
              borderColor: selectedMuni ? 'rgba(93,193,185,0.4)' : 'rgba(255,255,255,0.1)',
            }}
          />
          {selectedMuni && (
            <span style={{ position: 'absolute', right: 14, top: 38, fontSize: 12, color: '#5dc1b9' }}>✓</span>
          )}
          {showMuniDropdown && muniResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: 'rgba(20,20,30,0.98)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 12, marginTop: 4, maxHeight: 220, overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}>
              {muniResults.map((m, i) => (
                <button key={i} onClick={() => handleMuniSelect(m)} style={{
                  display: 'block', width: '100%', padding: '10px 14px', background: 'transparent',
                  border: 'none', borderBottom: i < muniResults.length - 1 ? '0.5px solid rgba(255,255,255,0.06)' : 'none',
                  color: '#fff', fontSize: 13, textAlign: 'left', cursor: 'pointer', fontFamily: font,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(93,193,185,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fallback for non-Mexico: simple text input */}
      {value.regionId && !isMexico && (
        <div>
          <p style={labelStyle}>{labels.city}</p>
          <input
            type="text"
            value={muniQuery}
            onChange={(e) => { setMuniQuery(e.target.value); onChange({ ...value, cityName: e.target.value }); }}
            placeholder="Escribe tu ciudad"
            style={inputStyle}
          />
        </div>
      )}
    </div>
  );
}
